import Cancel from '@mui/icons-material/Cancel';
import Warning from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { t } from 'i18next';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { ComplianceIssue } from '~hooks/useComplianceCheck/models';
import { ComplianceCheckResult } from '~hooks/useComplianceCheck/useComplianceCheck';

interface Props {
  isOpen: boolean;
  result: ComplianceCheckResult;
  isSubmitting?: boolean;
  onCancel: () => void;
  onOverride: () => void;
}

export function ComplianceWarningDialog({
  isOpen,
  result,
  isSubmitting = false,
  onCancel,
  onOverride,
}: Props) {
  const allIssues = [...result.blocking, ...result.warnings];
  const grouped = groupIssuesByType(allIssues);

  return (
    <Dialog isOpen={isOpen} maxWidth="xs">
      <DialogHeader onClose={onCancel}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning sx={{ color: 'warning.main' }} />
          <Typography variant="h6">{t('dispatch.compliance.warning_title')}</Typography>
        </Box>
      </DialogHeader>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('dispatch.compliance.warning_text')}
        </Typography>

        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
          {result.error && grouped.length === 0 ? (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('dispatch.compliance.check_unavailable')}
            </Typography>
          ) : (
            grouped.map((group) => (
              <Box key={group.label} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {group.label}
                </Typography>

                {group.issues.map((issue, index) => (
                  <IssueRow key={index} issue={issue} />
                ))}
              </Box>
            ))
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <SmallButton
          color="secondary"
          disabled={isSubmitting}
          onClick={onCancel}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>
        <SmallLoadingButton
          color="primary"
          loading={isSubmitting}
          onClick={onOverride}
          variant="contained"
        >
          {t('dispatch.compliance.assign_anyway')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export function getIssueStatusText(issue: ComplianceIssue): string {
  const isExpiring = issue.type === 'expiring';
  const isExpired = issue.type === 'expired';

  if (isExpired && issue.expiresAt) {
    const formatted = dayjs.tz(issue.expiresAt).format('MMM-DD-YY');
    return `${t('dispatch.compliance.status_expired')} (${formatted})`;
  }
  if (isExpiring && issue.expiresAt) {
    const daysUntil = dayjs.tz(issue.expiresAt).diff(dayjs.tz(), 'day', true);
    const daysUntilCeil = Math.ceil(daysUntil);
    const formatted = dayjs.tz(issue.expiresAt).format('MMM-DD-YY');
    return `${t('dispatch.compliance.status_expiring_in', { days: daysUntilCeil })} (${formatted})`;
  }
  return t('dispatch.compliance.status_missing');
}

function IssueRow({ issue }: { issue: ComplianceIssue }) {
  const isExpiring = issue.type === 'expiring';
  const statusText = getIssueStatusText(issue);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, pl: 1 }}>
      {isExpiring ? (
        <Warning sx={{ color: 'warning.main', fontSize: 18 }} />
      ) : (
        <Cancel sx={{ color: 'error.main', fontSize: 18 }} />
      )}
      <Box>
        <Typography variant="body2">{issue.documentType}</Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          {statusText}
        </Typography>
      </Box>
    </Box>
  );
}

interface GroupedIssues {
  label: string;
  issues: ComplianceIssue[];
}

export function groupIssuesByType(issues: ComplianceIssue[]): GroupedIssues[] {
  const vendorIssues: ComplianceIssue[] = [];
  const driverIssues: ComplianceIssue[] = [];
  const equipmentIssues: ComplianceIssue[] = [];

  for (const issue of issues) {
    const entity = issue.entity ?? '';
    if (entity === 'User') {
      driverIssues.push(issue);
    } else if (entity === 'Equipment') {
      equipmentIssues.push(issue);
    } else {
      vendorIssues.push(issue);
    }
  }

  const groups: GroupedIssues[] = [];
  if (vendorIssues.length > 0) {
    groups.push({ label: t('dispatch.compliance.vendor_issues'), issues: vendorIssues });
  }
  if (driverIssues.length > 0) {
    groups.push({ label: t('dispatch.compliance.driver_issues'), issues: driverIssues });
  }
  if (equipmentIssues.length > 0) {
    groups.push({
      label: t('dispatch.compliance.equipment_issues'),
      issues: equipmentIssues,
    });
  }
  return groups;
}
